import { useInfiniteQuery } from '@tanstack/react-query';
import { ChevronLeft, Loader2, Star, Store } from 'lucide-react';

import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/shared/components/ui/drawer';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useServices } from '@/shared/hooks/use-services';
import { cn } from '@/shared/lib/utils';

import {
  STORE_REVIEWS_PER_PAGE,
  type GetStoreReviewsResponse,
  type StoreReview,
} from '../types/get-store-reviews';

const RATING_MAX = 5;

interface StoreReviewsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  slug?: string;
  merchantName?: string;
}

function formatAverage(average: string | undefined) {
  const value = Number(average);
  if (!average || Number.isNaN(value) || value <= 0) return '--';
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function formatDate(date: string) {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Stars({ value, size = 'w-3.5 h-3.5' }: { value: number; size?: string }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`Nota ${value} de ${RATING_MAX}`}>
      {Array.from({ length: RATING_MAX }, (_, i) => i + 1).map(star => (
        <Star
          key={star}
          className={cn(size, star <= value ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300')}
        />
      ))}
    </div>
  );
}

function ReviewItem({ review, merchantName }: { review: StoreReview; merchantName?: string }) {
  return (
    <li className="flex flex-col gap-2 py-4 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold text-night">{review.customer_name}</span>
        <span className="text-[11px] text-gray-400">{formatDate(review.created_at)}</span>
      </div>
      <Stars value={review.rating} />
      {review.comment_hidden ? (
        <p className="text-xs italic text-gray-400">Comentário ocultado pela loja</p>
      ) : (
        review.comment && (
          <p className="text-xs text-night whitespace-pre-line break-words">{review.comment}</p>
        )
      )}
      {review.merchant_reply && (
        <div className="mt-1 flex flex-col gap-1 rounded-2xl bg-muted px-4 py-3">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-night">
            <Store className="w-3.5 h-3.5 text-primary" />
            {merchantName ? `Resposta de ${merchantName}` : 'Resposta do estabelecimento'}
          </span>
          <p className="text-xs text-night whitespace-pre-line break-words">
            {review.merchant_reply.message}
          </p>
        </div>
      )}
    </li>
  );
}

/**
 * Lista de avaliações da loja (PRAT-43): resumo com a média dos últimos 30 dias
 * e as avaliações, mais recentes primeiro, carregadas de página em página.
 */
export function StoreReviewsDrawer({
  isOpen,
  onClose,
  slug,
  merchantName,
}: StoreReviewsDrawerProps) {
  const { getStoreReviews } = useServices();

  const reviewsQuery = useInfiniteQuery({
    queryKey: ['store-reviews', slug],
    queryFn: ({ pageParam }) =>
      getStoreReviews.execute({
        slug: slug!,
        page: pageParam as number,
        per_page: STORE_REVIEWS_PER_PAGE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: GetStoreReviewsResponse) =>
      lastPage.pagination.current_page < lastPage.pagination.last_page
        ? lastPage.pagination.current_page + 1
        : undefined,
    enabled: isOpen && !!slug,
  });

  const pages = reviewsQuery.data?.pages ?? [];
  const summary = pages[0]?.summary;
  const total = pages[0]?.pagination.total ?? 0;
  // PRAT-44: loja que desligou as avaliações públicas não mostra nota nem lista.
  const reviewsEnabled = pages[0]?.reviews_enabled ?? true;
  const hiddenCount = pages[0]?.hidden_comments_count ?? 0;
  const reviews = pages.flatMap(page => page.reviews);

  return (
    <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
      <DrawerContent className="flex flex-col max-h-[85vh] w-full">
        <DrawerTitle className="sr-only">Avaliações da loja</DrawerTitle>
        <DrawerDescription className="sr-only">
          Nota média dos últimos 30 dias e avaliações dos clientes
        </DrawerDescription>

        {/* Header */}
        <div className="relative flex gap-4 items-center w-full px-6 py-6 flex-shrink-0 border-b border-gray-100">
          <button
            onClick={onClose}
            className="z-10 cursor-pointer clickable-small"
            aria-label="Fechar avaliações"
          >
            <ChevronLeft className="text-primary h-6 w-6" />
          </button>
          <p className="font-bold uppercase text-xs absolute left-1/2 -translate-x-1/2 w-full text-center">
            AVALIAÇÕES
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {reviewsQuery.isLoading && (
            <div className="flex flex-col gap-4">
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-20 w-full rounded-2xl" />
              <Skeleton className="h-20 w-full rounded-2xl" />
            </div>
          )}

          {reviewsQuery.isError && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <p className="text-sm text-gray-600">Não foi possível carregar as avaliações.</p>
              <Button variant="outline" onClick={() => reviewsQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}

          {summary && (
            <div className="flex items-center gap-4 rounded-2xl bg-muted px-4 py-4">
              <span className="text-3xl font-bold text-night">
                {formatAverage(summary.average)}
              </span>
              <div className="flex flex-col gap-1">
                <Stars value={Math.round(Number(summary.average) || 0)} size="w-4 h-4" />
                <span className="text-xs text-gray-500">
                  {summary.count > 0
                    ? `${summary.count} ${summary.count === 1 ? 'avaliação' : 'avaliações'} nos últimos ${summary.period_days} dias`
                    : `Sem avaliações nos últimos ${summary.period_days} dias`}
                </span>
              </div>
            </div>
          )}

          {reviewsQuery.isSuccess && !reviewsEnabled && (
            <p className="py-8 text-center text-sm text-gray-500">
              Esta loja não exibe avaliações.
            </p>
          )}

          {reviewsQuery.isSuccess && reviewsEnabled && reviews.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">
              Esta loja ainda não recebeu avaliações.
            </p>
          )}

          {reviews.length > 0 && (
            <ul className="mt-2 flex flex-col">
              {reviews.map(review => (
                <ReviewItem key={review.id} review={review} merchantName={merchantName} />
              ))}
            </ul>
          )}

          {hiddenCount > 0 && (
            <p className="py-3 text-center text-xs text-gray-500">
              {hiddenCount === 1
                ? '1 comentário ocultado pela loja'
                : `${hiddenCount} comentários ocultados pela loja`}
            </p>
          )}

          {reviewsQuery.hasNextPage && (
            <div className="pt-2 pb-6">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => reviewsQuery.fetchNextPage()}
                disabled={reviewsQuery.isFetchingNextPage}
              >
                {reviewsQuery.isFetchingNextPage ? (
                  <>
                    <Loader2 className="animate-spin" />
                    Carregando...
                  </>
                ) : (
                  `Ver mais avaliações (${reviews.length} de ${total})`
                )}
              </Button>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
